import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toNumber, toString, toSafeInteger } from 'lodash';
import {
  Form,
  Button,
  Modal,
  Header,
  Icon,
  Select,
  Message,
} from 'semantic-ui-react';
import { MILES_SELECTED_OPTIONS, DAYS_SELECTED_OPTIONS } from '../../constants';

import { utcMoment, dateTimeFormatted } from '../../utilities/dateUtils';
import { kindFormatted } from '../../utilities/productUtil';

class GuaranteePurchaseEdit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      id: '',
      dealerNumber: '',
      auctionAccessNumber: '',
      vehiclePurchasePrice: '',
      buyFee: '',
      year: '',
      make: '',
      model: '',
      odometerReading: '',
      partner: '',
      kind: '',
      milesSelected: '',
      daysSelected: '',
      transportReimbursement: '',
      emails: '',
      guaranteedAt: '',
      expiresAt: '',
      guaranteePrice: '',
      productName: '',
    };
  }

  componentDidMount() {
    const { guaranteePurchase } = this.props;
    const {
      id,
      kind,
      partner,
      guaranteedAt,
      expiresAt,
      guaranteeOffer: {
        emails,
        daysSelected,
        milesSelected,
        transportReimbursement,
        guaranteePrice,
        product,
        vehicleInformation: {
          make, model, odometerReading, year,
        },
        auctionInformation: {
          buyFee,
          dealerNumber,
          auctionAccessNumber,
          vehiclePurchasePrice,
        },
      },
    } = guaranteePurchase;

    const getProductName = prod => {
      return prod
        ? prod.name
        : `${kindFormatted(kind)} (${daysSelected} D ${milesSelected} M)`;
    };

    this.setState({
      id: toNumber(id),
      dealerNumber: toNumber(dealerNumber),
      auctionAccessNumber: toNumber(auctionAccessNumber),
      buyFee: toNumber(buyFee),
      vehiclePurchasePrice: toNumber(vehiclePurchasePrice),
      year: toNumber(year),
      make: toString(make),
      model: toString(model),
      odometerReading: toNumber(odometerReading),
      partner: toString(partner),
      kind: toString(kind),
      milesSelected: toNumber(milesSelected),
      daysSelected: toNumber(daysSelected),
      transportReimbursement: toSafeInteger(transportReimbursement),
      emails: toString(emails),
      guaranteedAt,
      expiresAt: dateTimeFormatted(utcMoment(expiresAt)),
      guaranteePrice: toNumber(guaranteePrice),
      productName: toString(getProductName(product)),
    });
  }

  render() {
    const {
      guaranteePurchase,
      isModalOpen,
      onCloseHandler,
      onSubmitHandler,
      error,
      submitting,
    } = this.props;

    const {
      year,
      make,
      model,
      odometerReading,
      dealerNumber,
      auctionAccessNumber,
      buyFee,
      vehiclePurchasePrice,
      guaranteedAt,
      expiresAt,
      partner,
      productName,
      milesSelected,
      daysSelected,
      guaranteePrice,
      transportReimbursement,
      emails,
    } = this.state;

    const onChangeHandler = field => event => {
      let { value } = event.target;
      const { min, max } = event.target;
      if (field === 'guaranteePrice') {
        value = Math.max(Number(min), Math.min(Number(max), Number(value)));
      }
      this.setState({ [field]: value });
    };
    const onSelectHandler = field => (event, data) => {
      this.setState({ [field]: data.value });
    };
    const onDateChangeHandler = field => date => {
      this.setState({ [field]: moment(date, 'YYYY-MM-DD HH:mm:ss') });
    };

    const submitForm = () => {
      const {
        id,
        dealerNumber: currentDealerNumber,
        auctionAccessNumber: currentAuctionAccessNumber,
        buyFee: currentBuyFee,
        vehiclePurchasePrice: currentVehiclePurchasePrice,
        year: currentYear,
        make: currentMake,
        model: currentModel,
        odometerReading: currentOdometerReading,
        partner: currentPartner,
        kind,
        milesSelected: currentMilesSelected,
        daysSelected: currentDaysSelected,
        transportReimbursement: currentTransportReimbursement,
        emails: currentEmails,
        guaranteedAt: currentGuaranteedAt,
        guaranteePrice: currentGuaranteePrice,
      } = this.state;

      onSubmitHandler({
        id: parseInt(id, 10),
        dealerNumber: parseInt(currentDealerNumber, 10),
        auctionAccessNumber: parseInt(currentAuctionAccessNumber, 10),
        buyFee: parseFloat(currentBuyFee || 0),
        vehiclePurchasePrice: parseFloat(currentVehiclePurchasePrice),
        year: parseInt(currentYear, 10),
        make: currentMake,
        model: currentModel || '',
        odometerReading: parseInt(currentOdometerReading, 10),
        partner: currentPartner || '',
        kind,
        milesSelected: parseInt(currentMilesSelected, 10),
        daysSelected: parseInt(currentDaysSelected, 10),
        transportReimbursement: parseFloat(currentTransportReimbursement),
        emails: currentEmails,
        guaranteedAt: currentGuaranteedAt,
        guaranteePrice: parseFloat(currentGuaranteePrice || 0),
      });
    };

    const {
      guaranteeOffer: {
        vehicleInformation: { vin },
      },
    } = guaranteePurchase;

    return (
      <Modal
        open={isModalOpen}
        closeIcon
        onClose={onCloseHandler}
        closeOnEscape={false}
        closeOnRootNodeClick={false}
      >
        <Modal.Header>Edit Guarantee Purchase for VIN {vin}</Modal.Header>
        <Modal.Content image scrolling>
          <Modal.Description>
            {error && (
              <Message negative>
                <Message.Header>
                  Error while updating guarantee purchase
                </Message.Header>
                <p>{error}</p>
              </Message>
            )}
            <Form>
              <Header size='small' color='blue'>
                Vehicle Details
              </Header>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='year'>Year</label>
                  <input
                    id='year'
                    name='year'
                    disabled
                    value={year}
                    onChange={onChangeHandler('year')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='make'>Make</label>
                  <input
                    id='make'
                    name='make'
                    disabled
                    value={make}
                    onChange={onChangeHandler('make')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='model'>Model</label>
                  <input
                    id='model'
                    name='model'
                    disabled
                    value={model}
                    onChange={onChangeHandler('model')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='odometerReading'>Odometer</label>
                  <input
                    id='odometerReading'
                    name='odometerReading'
                    disabled
                    value={odometerReading}
                    onChange={onChangeHandler('odometerReading')}
                  />
                </Form.Field>
              </Form.Group>
              <Header size='small' color='blue'>
                Auction Details
              </Header>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='dealerNumber'>Dealer Number</label>
                  <input
                    id='dealerNumber'
                    name='dealerNumber'
                    disabled
                    value={dealerNumber}
                    onChange={onChangeHandler('dealerNumber')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='auctionAccessNumber'>Auction Access Number</label>
                  <input
                    id='auctionAccessNumber'
                    name='auctionAccessNumber'
                    disabled
                    value={auctionAccessNumber}
                    onChange={onChangeHandler('auctionAccessNumber')}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='buyFee'>Buy Fee</label>
                  <input
                    id='buyFee'
                    name='buyFee'
                    disabled
                    value={buyFee}
                    onChange={onChangeHandler('buyFee')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='vehiclePurchasePrice'>Vehicle Purchase Price</label>
                  <input
                    id='vehiclePurchasePrice'
                    name='vehiclePurchasePrice'
                    disabled
                    value={vehiclePurchasePrice}
                    onChange={onChangeHandler('vehiclePurchasePrice')}
                  />
                </Form.Field>
              </Form.Group>
              <Header size='small' color='blue'>
                Guarantee Details
              </Header>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='guaranteedAt'>Guaranteed At</label>
                  <div>
                    <DatePicker
                      id='guaranteedAt'
                      style={{ padding: '0', width: '100%' }}
                      selected={utcMoment(guaranteedAt)}
                      onChange={onDateChangeHandler('guaranteedAt')}
                      showTimeSelect
                      timeFormat='HH:mm'
                      timeIntervals={15}
                      dateFormat='YYYY-MM-DD HH:mm:ss ZZ'
                      disabled
                    />
                  </div>
                </Form.Field>
                <Form.Field>
                  <label htmlFor='expiresAt'>Expiration Date</label>
                  <input
                    id='expiresAt'
                    name='expiresAt'
                    disabled
                    value={expiresAt}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='partner'>Partner Network</label>
                  <input
                    id='partner'
                    name='partner'
                    disabled
                    value={partner}
                    onChange={onChangeHandler('partner')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='productName'>Product</label>
                  <input id='productName' value={productName} disabled />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='milesSelected'>Miles Selected</label>
                  <Select
                    id='milesSelected'
                    placeholder='Select Miles'
                    name='milesSelected'
                    options={MILES_SELECTED_OPTIONS}
                    onChange={onSelectHandler('milesSelected')}
                    value={milesSelected}
                    disabled
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='daysSelected'>Days Selected</label>
                  <Select
                    id='daysSelected'
                    placeholder='Select Days'
                    name='daysSelected'
                    options={DAYS_SELECTED_OPTIONS}
                    onChange={onSelectHandler('daysSelected')}
                    value={daysSelected}
                    disabled
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label htmlFor='guaranteePrice'>Guarantee Price</label>
                  <input
                    id='guaranteePrice'
                    name='guaranteePrice'
                    value={guaranteePrice}
                    onChange={onChangeHandler('guaranteePrice')}
                    type='number'
                    min='0'
                    max='9999'
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='transportReimbursement'>Transport Reimbursement</label>
                  <input
                    id='transportReimbursement'
                    name='transportReimbursement'
                    disabled
                    value={transportReimbursement}
                    onChange={onChangeHandler('transportReimbursement')}
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor='emails'>Emails</label>
                  <input
                    id='emails'
                    name='emails'
                    value={emails}
                    onChange={onChangeHandler('emails')}
                  />
                </Form.Field>
              </Form.Group>
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          {submitting && (
            <Button loading positive>
              Submitting...
            </Button>
          )}
          {!submitting && (
            <Button color='green' onClick={submitForm} inverted>
              <Icon name='checkmark' /> Update
            </Button>
          )}
          <Button onClick={onCloseHandler}>Cancel</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

GuaranteePurchaseEdit.propTypes = {
  guaranteePurchase: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    kind: PropTypes.string,
    partner: PropTypes.string,
    guaranteedAt: PropTypes.string,
    expiresAt: PropTypes.string,
    guaranteeOffer: PropTypes.shape({
      emails: PropTypes.string,
      daysSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      milesSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      transportReimbursement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      guaranteePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      product: PropTypes.shape({
        name: PropTypes.string,
      }),
      vehicleInformation: PropTypes.shape({
        make: PropTypes.string,
        model: PropTypes.string,
        odometerReading: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vin: PropTypes.string,
      }),
      auctionInformation: PropTypes.shape({
        buyFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dealerNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        auctionAccessNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vehiclePurchasePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    }),
  }),
  isModalOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func,
  onSubmitHandler: PropTypes.func,
  error: PropTypes.string,
  submitting: PropTypes.bool,
};

export default GuaranteePurchaseEdit;
